// client/src/pages/AdminCardOrdersPage.jsx
// Super-admin triage panel for the card-order pipeline. Lists every
// order across every camp; lets the project owner move them through
// SUBMITTED → PROCESSING → SHIPPED (or CANCELLED), download each
// uploaded logo, and reach the camp / submitting manager.
//
// Auth: gated server-side by requireSuperAdmin. Anyone else gets 403.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useAllCardOrders,
  useUpdateCardOrderStatus,
} from '../hooks/useAdminCardOrders.js';
import Spinner from '../components/Spinner.jsx';
import { formatDateTime } from '../lib/format.js';
import { CreditCardIcon, ArrowDownTrayIcon } from '../components/Icons.jsx';

const STATUS_LABEL = {
  SUBMITTED:  { text: 'נשלחה',      cls: 'badge-amber' },
  PROCESSING: { text: 'בעיבוד',     cls: 'badge-sky' },
  SHIPPED:    { text: 'נשלחה אליו',  cls: 'badge-mint' },
  CANCELLED:  { text: 'בוטלה',      cls: 'badge-slate' },
};

const STATUS_FILTERS = [
  { value: '',           label: 'הכל' },
  { value: 'SUBMITTED',  label: 'חדשות'  },
  { value: 'PROCESSING', label: 'בעיבוד' },
  { value: 'SHIPPED',    label: 'נשלחו'  },
  { value: 'CANCELLED',  label: 'בוטלו'  },
];

export default function AdminCardOrdersPage() {
  const [status, setStatus] = useState('');
  const ordersQ = useAllCardOrders(status ? { status } : {});
  const updateMut = useUpdateCardOrderStatus();

  const list = ordersQ.data;

  return (
    <div className="space-y-6">
      {/* Hero */}
      <header className="card-pop p-6 sm:p-8 relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-1.5 bg-brand-gradient" />
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-2xl bg-brand-gradient text-white grid place-items-center shrink-0 shadow-pop">
            <CreditCardIcon className="w-7 h-7" />
          </div>
          <div className="min-w-0">
            <p className="eyebrow">ניהול הזמנות</p>
            <h1 className="h-page">הזמנות כרטיסים — כל התוכניות</h1>
            <p className="mt-1.5 text-muted">
              סקירה של כל ההזמנות שנשלחו מבית הספרים שמשתמשים ב-TicTac.
              ניתן לעדכן סטטוס, להוריד את הלוגו שהועלה, ולפנות למזמין.
            </p>
          </div>
        </div>
      </header>

      {/* Status filter chips */}
      <div className="card p-3 sm:p-4 flex flex-wrap gap-1.5 items-center">
        <span className="text-xs font-bold text-ink-500 uppercase tracking-wider me-1">סטטוס</span>
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value || 'all'}
            type="button"
            onClick={() => setStatus(f.value)}
            className={status === f.value ? 'chip-active' : 'chip'}
          >
            {f.label}
          </button>
        ))}
        {list && (
          <span className="ms-auto text-sm text-ink-500">
            סה״כ <strong className="text-ink-700 num">{list.total.toLocaleString('he-IL')}</strong>
          </span>
        )}
      </div>

      {ordersQ.isLoading && !list ? (
        <Spinner label="טוען הזמנות..." />
      ) : ordersQ.isError ? (
        <div className="card-danger p-4 text-joy-900 text-sm">
          שגיאה: {ordersQ.error.message}
        </div>
      ) : list.orders.length === 0 ? (
        <EmptyState hasFilter={!!status} />
      ) : (
        <ul className="card divide-y divide-ink-100 overflow-hidden">
          {list.orders.map((o) => (
            <OrderRow
              key={o.id}
              o={o}
              onStatus={(next) => updateMut.mutate({ orderId: o.id, status: next })}
              busy={updateMut.isPending}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function OrderRow({ o, onStatus, busy }) {
  const st = STATUS_LABEL[o.status] || STATUS_LABEL.SUBMITTED;
  return (
    <li className="p-4 sm:p-5">
      <div className="flex items-start gap-3 flex-wrap">
        {/* Direction stripe */}
        <div className="w-1.5 self-stretch rounded-full bg-brand-500" aria-hidden />
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to={`/camps/${o.campId}`}
              className="font-bold text-ink-900 hover:text-brand-700 transition-colors"
            >
              {o.camp?.name || '—'}
            </Link>
            <span className={`${st.cls} text-xs`}>{st.text}</span>
            <span className="badge-slate text-xs num">
              {o.qtyTotal.toLocaleString('he-IL')} כרטיסים
            </span>
            {o.printName && <span className="badge-slate text-xs">עם שמות</span>}
            {o.shippingMethod === 'EXPRESS' && (
              <span className="badge-slate text-xs">אקספרס</span>
            )}
          </div>
          <div className="text-xs text-ink-500 flex flex-wrap items-center gap-x-2 num">
            <span>{formatDateTime(o.createdAt)}</span>
            <span aria-hidden>·</span>
            <span>{o.createdBy?.name || o.createdBy?.email || '—'}</span>
            <span aria-hidden>·</span>
            <span dir="ltr">{o.createdBy?.email}</span>
          </div>
          <div className="text-xs text-ink-600">
            <span className="font-semibold">בירורים:</span>{' '}
            <span dir="ltr" className="num">{o.inquiryPhone}</span>
          </div>
          {o.shippingMethod === 'EXPRESS' && (
            <div className="text-xs text-ink-600 leading-relaxed mt-1 bg-ink-50/60 rounded-xl p-2">
              <div>
                <strong>למשלוח:</strong> {o.shippingRecipient} · {o.shippingAddress}, {o.shippingCity}
              </div>
              <div className="num mt-0.5">
                {o.shippingPhone}{o.shippingPhone2 ? ` / ${o.shippingPhone2}` : ''}
                {o.shippingDoorOk === 'YES' && (
                  <> · ליד הדלת: {o.shippingDoorComment}</>
                )}
                {o.shippingDoorOk === 'NO' && <> · לא להשאיר ליד הדלת</>}
              </div>
            </div>
          )}
          <div className="text-xs text-ink-600 mt-1">
            <span className="font-semibold">תצוגה:</span>{' '}
            {o.schoolDisplay === 'NAME' && <>שם — {o.schoolDisplayName}</>}
            {o.schoolDisplay === 'LOGO' && <>לוגו</>}
            {o.schoolDisplay === 'NONE' && <>ללא</>}
            {' · '}
            <span className="font-semibold">מודפס:</span>{' '}
            {[
              o.printName  && 'שם',
              o.printClass && 'כיתה',
              o.printPhone && 'טלפון',
            ].filter(Boolean).join(' · ') || '—'}
          </div>
          {o.notes && (
            <div className="text-xs text-ink-700 mt-1 bg-gold-50 border border-gold-200 rounded-xl p-2">
              <strong className="text-gold-800">הערה:</strong> {o.notes}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {o.hasLogo ? (
              <a
                href={`/api/admin/card-orders/${o.id}/logo`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary btn-sm"
              >
                <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                לוגו: {o.logoFilename}
              </a>
            ) : o.logoFilename ? (
              <span className="text-xs text-ink-400">לוגו: {o.logoFilename} (לא נשמר — הזמנה ישנה)</span>
            ) : null}
            <code className="text-[11px] font-mono text-ink-500" dir="ltr">{o.id}</code>
          </div>
        </div>
        <StatusActions
          current={o.status}
          onChange={onStatus}
          busy={busy}
        />
      </div>
    </li>
  );
}

function StatusActions({ current, onChange, busy }) {
  const next = {
    SUBMITTED:  ['PROCESSING', 'CANCELLED'],
    PROCESSING: ['SHIPPED',    'CANCELLED'],
    SHIPPED:    [],
    CANCELLED:  ['SUBMITTED'],
  }[current] || [];
  if (next.length === 0) return null;
  return (
    <div className="flex flex-col gap-1.5 shrink-0">
      {next.map((s) => (
        <button
          key={s}
          type="button"
          disabled={busy}
          onClick={() => onChange(s)}
          className={
            s === 'CANCELLED'
              ? 'btn-secondary btn-sm whitespace-nowrap'
              : 'btn-pop btn-sm whitespace-nowrap'
          }
        >
          {STATUS_ACTION_LABEL[s]}
        </button>
      ))}
    </div>
  );
}

const STATUS_ACTION_LABEL = {
  SUBMITTED:  'החזר לחדשות',
  PROCESSING: 'התחל עיבוד',
  SHIPPED:    'סמן כנשלח',
  CANCELLED:  'בטל',
};

function EmptyState({ hasFilter }) {
  return (
    <div className="card-pop p-10 sm:p-14 text-center max-w-xl mx-auto">
      <div className="mx-auto w-16 h-16 rounded-3xl bg-brand-gradient text-white grid place-items-center mb-4 shadow-pop">
        <CreditCardIcon className="w-8 h-8" />
      </div>
      <h2 className="text-lg font-bold text-ink-900">
        {hasFilter ? 'אין הזמנות בסטטוס הזה' : 'עוד לא הוזמנו כרטיסים'}
      </h2>
      <p className="mt-2 text-muted">
        {hasFilter
          ? 'נסה לבחור סטטוס אחר.'
          : 'הזמנות יופיעו כאן ברגע שמנהל בבית ספר ישלח בקשה.'}
      </p>
    </div>
  );
}
